/**
 * @file Source-quote popover for extracted-property page badges.
 *
 * **Canonical pattern: popover-inside-Sheet collision.**
 *
 * When a base-ui Popover is rendered inside our shadcn Sheet (e.g., the
 * mobile DocumentDetailPanel), both register `pointerdown` on `document`.
 * Listener order is non-deterministic, so the Sheet's backdrop handler can
 * fire on the same dismiss event that closes the Popover, silently
 * collapsing the Sheet too.
 *
 * Resolution: use raw `@base-ui/react/popover` primitives (NOT the shadcn
 * wrapper, which doesn't expose enough surface to intercept onOpenChange's
 * event arg). Render with `modal` so outside pointer events are blocked
 * while open, then in `onOpenChange` intercept the `outside-press` and
 * `escape-key` reasons and call `eventDetails.event?.preventDefault()` so
 * the Sheet's own listener bails on `defaultPrevented`. Add a capture-phase
 * pointerdown guard for belt-and-suspenders (covers the rare case the
 * parent ignores `defaultPrevented`).
 *
 * Live precedents:
 *   - `app/src/components/frontend/chat/ShareConversationPopover.tsx`
 *   - this file (extraction page-badge popover)
 *
 * Reuse this pattern for ANY popover / select / menu rendered inside a
 * Sheet, Drawer, or other portalled overlay that owns its own
 * outside-press dismiss.
 */
import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import { ExternalLink } from "lucide-react";
import {
    type ReactElement,
    type ReactNode,
    useEffect,
    useRef,
    useState,
} from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Button } from "@/components/ui/button";
import { sourceQuoteMarkdownComponents } from "./sourceQuoteMarkdown";

export interface PdfSourceModalPayload {
    documentId: number;
    propertyId: number;
    pageNumber: number;
    fieldName: string;
    source: string | null;
}

// Cap the preview at this many characters before handing off to markdown.
// Truncating before parsing means a long chunk doesn't blow up the popover
// height; users still see the full text via "Open in document".
const SOURCE_PREVIEW_MAX_CHARS = 240;

// Truncate to whole-word boundaries: trim back to the last whitespace within
// the cap and append `…`. Falls back to a hard cut for pathological inputs
// with no whitespace in the head slice.
function truncateAtWord(text: string, maxChars: number): string {
    if (text.length <= maxChars) return text;
    const slice = text.slice(0, maxChars);
    return `${slice.replace(/\s\S*$/, "").trimEnd()}…`;
}

export interface SourceQuotePopoverProps {
    documentId: number;
    propertyId: number;
    fieldName: string;
    pageNumber: number;
    source: string | null;
    onOpenInDocument: (payload: PdfSourceModalPayload) => void;
    /**
     * The trigger element rendered inline (typically the page badge button).
     * Base-ui's `Popover.Trigger` clones it and merges in the open/close
     * behavior plus aria attributes, so the caller keeps control of the
     * trigger's visual styling.
     */
    children: ReactElement | ReactNode;
}

/**
 * Source-quote popover anchored to the page badge.
 *
 * Two collisions to be aware of:
 *
 * 1. **Mobile Sheet collision.** The panel renders inside a `Sheet` on mobile.
 *    Base-ui's portalled popover and the Sheet's backdrop both register
 *    `pointerdown` on `document` and the firing order depends on render order,
 *    which is non-deterministic. Without an explicit guard, an outside-press
 *    that dismisses the popover would also collapse the parent Sheet. The
 *    `onOpenChange` handler below intercepts the `outside-press` and
 *    `escape-key` reasons and calls `eventDetails.event.preventDefault()` so
 *    the Sheet's own listener bails on `defaultPrevented`. **Live precedent:**
 *    `app/src/components/frontend/chat/ShareConversationPopover.tsx`.
 *
 * 2. **Trigger-press absorbing the row's click handler.** The badge becomes
 *    the popover trigger, but the value text (a sibling) owns the
 *    click-to-edit affordance. Because they're DOM siblings, base-ui's open
 *    click never bubbles to the value button, but `eventDetails.event` is
 *    still preventDefault-ed defensively in case a future row layout makes
 *    the badge a descendant of the value-stack column.
 */
export function SourceQuotePopover({
    documentId,
    propertyId,
    fieldName,
    pageNumber,
    source,
    onOpenInDocument,
    children,
}: SourceQuotePopoverProps) {
    const [open, setOpen] = useState(false);
    const popupRef = useRef<HTMLDivElement>(null);

    const trimmedSource = source?.trim() ?? "";
    const hasSource = trimmedSource.length > 0;
    const displaySource = truncateAtWord(
        trimmedSource,
        SOURCE_PREVIEW_MAX_CHARS
    );

    // Capture-phase pointerdown guard. Runs before bubble-phase document
    // listeners (e.g., a parent `Sheet`'s backdrop dismiss). When the click
    // lands outside the popup, we mark the event "handled" so the Sheet's
    // listener bails on `defaultPrevented` and doesn't collapse beneath us.
    // The base-ui dismiss path still fires from the popover's own listeners
    // (registered separately), so the popover still closes.
    useEffect(() => {
        if (!open) return;
        const onPointerDownCapture = (e: PointerEvent) => {
            const target = e.target as Node | null;
            // Inside *this* popup → not an outside-press; let the popup's own
            // handlers run. Match by node containment, not by `[role="dialog"]`
            // ancestry; the latter would also match unrelated dialogs sharing
            // the page (e.g., a confirmation dialog opened over the panel).
            if (target && popupRef.current?.contains(target)) {
                return;
            }
            // Outside-press: pre-empt parent Sheet listeners.
            e.preventDefault();
        };
        document.addEventListener("pointerdown", onPointerDownCapture, {
            capture: true,
        });
        return () =>
            document.removeEventListener("pointerdown", onPointerDownCapture, {
                capture: true,
            });
    }, [open]);

    const handleOpenChange = (
        nextOpen: boolean,
        eventDetails: PopoverPrimitive.Root.ChangeEventDetails
    ) => {
        if (!nextOpen) {
            // Mark the dismissal "handled" so a Sheet whose backdrop listener
            // also fires on this same `pointerdown` (mobile) bails on
            // `defaultPrevented`. Esc gets the same treatment so a panel-level
            // Esc handler doesn't also fire and close the panel beneath us.
            if (
                eventDetails.reason === "outside-press" ||
                eventDetails.reason === "escape-key"
            ) {
                eventDetails.event?.preventDefault();
            }
        }
        setOpen(nextOpen);
    };

    const handleOpenInDocument = () => {
        onOpenInDocument({
            documentId,
            propertyId,
            pageNumber,
            fieldName,
            source,
        });
        setOpen(false);
    };

    return (
        <PopoverPrimitive.Root
            open={open}
            onOpenChange={handleOpenChange}
            // `modal` blocks pointer interactions on outside elements while the
            // popover is open, so the parent `Sheet`'s document-level
            // pointerdown listener never sees the dismiss click and can't
            // collapse on it. `trap-focus` is intentionally NOT used because
            // it allows outside pointer events through, which is exactly the
            // mobile collision we're guarding against.
            modal
        >
            <PopoverPrimitive.Trigger render={children as ReactElement} />
            <PopoverPrimitive.Portal>
                <PopoverPrimitive.Positioner
                    side="bottom"
                    align="start"
                    sideOffset={6}
                    className="isolate z-50"
                >
                    <PopoverPrimitive.Popup
                        ref={popupRef}
                        className="bg-popover text-popover-foreground ring-foreground/10 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 motion-reduce:zoom-in-100 w-[20rem] max-w-[calc(100vw-1.5rem)] origin-(--transform-origin) rounded-xl p-3 text-sm shadow-lg ring-1 outline-hidden duration-150"
                        role="dialog"
                        aria-label={`Source for ${fieldName}`}
                    >
                        <div className="flex flex-col gap-2.5">
                            <span className="text-muted-foreground text-[11px] font-semibold tracking-wider uppercase">
                                Source · Page {pageNumber}
                            </span>
                            {hasSource ? (
                                // Chunk text comes through as markdown
                                // (lists, bold, occasional tables).
                                // `displaySource` is whole-word truncated
                                // before parsing; the renderer is tolerant
                                // of any unclosed inline syntax left at the
                                // tail.
                                <div className="text-foreground text-[13px] leading-relaxed break-words">
                                    <Markdown
                                        remarkPlugins={[remarkGfm]}
                                        components={
                                            sourceQuoteMarkdownComponents
                                        }
                                    >
                                        {displaySource}
                                    </Markdown>
                                </div>
                            ) : (
                                <p className="text-muted-foreground text-[13px] leading-relaxed">
                                    Source on page {pageNumber}
                                </p>
                            )}
                            <div className="flex justify-end">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleOpenInDocument}
                                    className="text-primary hover:text-primary hover:bg-primary/10 h-7 gap-1.5 px-2 text-[12px] font-medium"
                                >
                                    <ExternalLink
                                        aria-hidden
                                        className="size-3.5"
                                    />
                                    Open in document
                                </Button>
                            </div>
                        </div>
                    </PopoverPrimitive.Popup>
                </PopoverPrimitive.Positioner>
            </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
    );
}
